import { useEffect } from "react";
import Lenis from "lenis";

/**
 * Inertia scrolling on desktop pointers only (Lenis). Phones, coarse pointers and
 * prefers-reduced-motion keep the native scroll. Nested scroll areas (calendar drawers, popovers,
 * the chat) scroll natively; in-page anchors are eased. Exposed on `window.__lenis` so the router
 * can jump to the top instantly on navigation.
 */
export const SmoothScroll = () => {
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    if (reduce || coarse || window.innerWidth < 1024) return undefined;

    const lenis = new Lenis({
      lerp: 0.085,
      smoothWheel: true,
      wheelMultiplier: 1,
      anchors: { offset: -96 },
      allowNestedScroll: true,
      stopInertiaOnNavigate: true,
    });
    window.__lenis = lenis;

    let raf = 0;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    // Radix locks the body while a dialog/drawer is open – pause the inertia so nothing drifts underneath.
    const sync = () => (document.body.hasAttribute("data-scroll-locked") ? lenis.stop() : lenis.start());
    const mo = new MutationObserver(sync);
    mo.observe(document.body, { attributes: true, attributeFilter: ["data-scroll-locked"] });

    return () => {
      mo.disconnect();
      cancelAnimationFrame(raf);
      lenis.destroy();
      if (window.__lenis === lenis) delete window.__lenis;
    };
  }, []);
  return null;
};

/** Jump to the top instantly, through Lenis when it is running. */
export const scrollToTopNow = () => {
  if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
  window.scrollTo({ top: 0, behavior: "instant" });
};
